import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { publishMessages } from '../publish-messages';
import { resolveUploadErrorMessage } from './resolve-upload-error-message';

interface UploadFailedErrorDetailsIconProps {
  /** API error message from failed upload data, if present. */
  error?: string;
  /** API error code from failed upload data, if present. */
  errorCode?: string;
}

/**
 * Info icon with tooltip showing failed-upload error message and/or code.
 * Known API keys (e.g. youtube_reconnect_required) are shown as translated copy.
 */
export function UploadFailedErrorDetailsIcon({
  error,
  errorCode,
}: UploadFailedErrorDetailsIconProps) {
  const { $t } = useIntl();

  const knownMessage = resolveUploadErrorMessage(error, errorCode);
  const displayError = knownMessage ? $t(knownMessage) : error;
  // Skip the code line when errorCode itself was mapped to the user-facing message.
  const displayErrorCode = resolveUploadErrorMessage(undefined, errorCode) ? undefined : errorCode;

  if (!displayError && !displayErrorCode) {
    return null;
  }

  return (
    <Tooltip
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {displayError ? (
            <Typography variant="body2" component="span">
              {displayError}
            </Typography>
          ) : null}
          {displayErrorCode ? (
            <Typography variant="caption" component="span">
              {$t(publishMessages.chipErrorCode, { errorCode: displayErrorCode })}
            </Typography>
          ) : null}
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: 'error.main',
          cursor: 'default',
        }}
      >
        <ErrorOutlineIcon fontSize="small" color="inherit" />
      </Box>
    </Tooltip>
  );
}
